import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import cmsClient from "src/services/cmsClient";
import { adaptAreaReport } from "src/features/reporting/adapters/";
import IndividualObservationTableCell from "../../components/IndividualObservationTableCell/IndividualObservationTableCell";
import StoreObservationTableCell from "../../components/StoreObservationTableCell/StoreObservationTableCell";
import GreatConversationTableCell from "../../components/GreatConversationTableCell/GreatConversationTableCell";

interface AreaReportOptions {
	year: number;
	formId: number;
	regionId: number;
	compliancy?: boolean;
	threshold?: boolean;
	observationType: IObservationType;
}

const columnHelper = createColumnHelper<any>();

const requestAreaReportExport = async (
	year: number,
	form: number,
	regionId: number,
	type: "compliant" | "complete" | "threshold"
) => {
	await cmsClient.post("/observations/reports/area-by-month/", {
		form_id: form,
		region_id: regionId,
		year,
		include_ignored: true,
		type
	});
};

const fetchAreaReport = async (
	year: number,
	form: number,
	regionId: number
) => {
	const response = await cmsClient.get<IAreaReport[]>(
		"/observations/reports/area-by-month/",
		{
			params: {
				year,
				form,
				region: regionId,
				includeIgnored: true
			}
		}
	);

	return response.data;
};

const useAreaReport = ({
	year,
	formId,
	regionId,
	compliancy,
	threshold,
	observationType
}: AreaReportOptions) => {
	const queryKey = ["area-report", { year, formId, regionId }];
	const queryFn = () => fetchAreaReport(year, formId, regionId);
	const select = (areaRepport: IAreaReport[]) => adaptAreaReport(areaRepport);

	const query = useQuery({ queryKey, queryFn, select });

	const months = Array.from({ length: 12 }, (_, i) => i).map((month) =>
		dayjs().month(month).format("MMM")
	);

	const monthlyColumns = months.map((month) => {
		return columnHelper.accessor(month, {
			cell: ({ cell, row }) => {
				if (observationType === "INDIVIDUAL") {
					return <IndividualObservationTableCell cellValue={cell.getValue()} />;
				}

				if (observationType === "GREAT_CONVERSATION") {
					return <GreatConversationTableCell cellValue={cell.getValue()} />;
				}

				return (
					<StoreObservationTableCell
						cellValue={cell.getValue()}
						showStatistics={true}
						areaId={row.original.areaId}
					/>
				);
			},
			header: month
		});
	});

	const columns = [
		columnHelper.display({
			id: "area-name",
			cell: ({ row }) => row.original.name,
			header: "Area"
		}),
		...monthlyColumns
	];

	const download = async () => {
		try {
			await requestAreaReportExport(
				year,
				formId,
				regionId,
				compliancy ? "compliant" : threshold ? "threshold" : "complete"
			);
			toast.success(
				<CustomToast
					type="Success"
					message="A link to your export (Area by month) has been generated. Check your mail inbox for the link."
				/>,
				{ hideProgressBar: true }
			);
		} catch (error) {
			toast.error(
				<CustomToast type="Error" message="Something went wrong, try again" />,
				{ hideProgressBar: true }
			);
		}
	};

	return { columns, download, ...query };
};

export default useAreaReport;
